import {
  Card,
  Group,
  MultiSelect,
  NumberInput,
  Select,
  Skeleton,
  Stack,
  Switch,
  Tabs,
  Text,
  Title,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import {
  ChatCircleIcon,
  GearSixIcon,
  ShieldCheckIcon,
  UsersThreeIcon,
  WarningIcon,
} from "@phosphor-icons/react"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import type { IGovernanceConfig } from "../../apis/governance-config"
import { useGovernanceConfigQuery, useSettingsMutations } from "./hooks"
import { ListState } from "../ui"
import { SETTINGS_WRITE, SettingsButton, SettingsFields } from "./settings-controls"

/* ── Role options (TODO: replace with real role API once de-user-service
      exposes a list endpoint) ───────────────────────────────────────── */
// Per PO walkthrough §2.2 — backend code stays ORDER_OPERATOR, UI shows
// "Хүргэлтийн супервайзер". Override via BusinessGovernanceConfig.roleLabels
// when tenant-specific labels are needed.
const ROLE_OPTION_VALUES = [
  "ORDER_OPERATOR",
  "ORDER_MANAGER",
  "DELIVERY_COORDINATOR",
  "PICKER",
  "DRIVER",
  "CASHIER",
  "WAREHOUSE_KEEPER",
] as const

/* Failure types whose photo requirement the business can set; `default` covers every other type.
   Keys are the BE failureEvidenceRules keys (de-order-service constants/GOVERNANCE_DEFAULTS.ts). */
const EVIDENCE_TYPES = ["DAMAGED_GOODS", "WRONG_GOODS", "ACCESS_BLOCKED", "NO_RESPONSE", "default"] as const
type EvidenceType = (typeof EVIDENCE_TYPES)[number]

/* BE enums (governance-config/upsert.ts Joi, contract §3). The first value of each list is the BE default. */
const ALLOCATION_STRATEGIES = ["HYBRID", "SOFT", "HARD"] as const
const SHORTAGE_POLICIES = ["SEVERITY_BASED", "MANUAL_REVIEW_ONLY", "AUTO_SPLIT_ONLY"] as const
const FEE_POLICIES = ["NEVER", "DRIVER_FAULT_ONLY", "ALWAYS"] as const
const TERMINATION_POLICIES = ["ESCALATION_LADDER", "IMMEDIATE_CANCEL", "MANUAL_ONLY"] as const
const SMS_OPERATORS = ["AUTO", "UNITEL", "MOBICOM", "SKYTEL"] as const

function evidenceFromConfig(rules: IGovernanceConfig["failureEvidenceRules"] | undefined): Record<EvidenceType, boolean> {
  const out = {} as Record<EvidenceType, boolean>
  for (const key of EVIDENCE_TYPES) out[key] = !!rules?.[key]?.requirePhoto
  return out
}

/* ── tiny helpers ──────────────────────────────────────── */
function SectionSave({
  onSave,
  loading,
}: {
  onSave: () => void
  loading: boolean
}) {
  const { t } = useTranslation("settings")
  return (
    <Group justify="flex-end" mt="sm">
      <SettingsButton code={SETTINGS_WRITE.governance} loading={loading} onClick={onSave}>
        {t("common.save")}
      </SettingsButton>
    </Group>
  )
}

/* ── component ─────────────────────────────────────────── */
export function GovernanceConfigTab() {
  const { t } = useTranslation("settings")
  const roleOptions = ROLE_OPTION_VALUES.map((value) => ({ value, label: t(`governance.roles.${value}`) }))
  const { data, isLoading, error, mutate: reload } = useGovernanceConfigQuery()
  const mutations = useSettingsMutations()

  /* Panel 1 — Core */
  const coreForm = useForm({
    initialValues: {
      inventoryAllocationStrategy: "HYBRID",
      buyerSmsNotificationsEnabled: false,
    },
  })

  /* Panel 2 — Shortage & failure (spec §2.7a item 6 sub-sections) */
  const shortageForm = useForm({
    initialValues: {
      shortageDecisionPolicy: "SEVERITY_BASED",
      shortageReviewThresholdPercent: 5,
      shortageEscalationThresholdPercent: 30,
      failedDeliveryFeePolicy: "NEVER",
      terminationPolicy: "ESCALATION_LADDER",
      terminationMaxAttempts: 3,
      evidence: evidenceFromConfig(undefined),
    },
  })

  /* Panel 3 — Idempotency (§26) */
  const idempotencyForm = useForm({
    initialValues: {
      keyTtlHours: 24,
      requireOnMutation: true,
      assignmentVersioning: false,
    },
  })

  /* Panel 4 — SMS gateway (§17) */
  const smsForm = useForm({
    initialValues: {
      enabled: false,
      operatorPreference: "AUTO",
    },
  })

  /* Panel 5 — Channel & Role Mapping (spec §2.7a item 6 last sub-section) */
  const rolesForm = useForm({
    initialValues: {
      routePlannerRoles:               [] as string[],
      supplyChannelCoordinatorRoles:   [] as string[],
      localChannelCoordinatorRoles:    [] as string[],
    },
  })

  /* Populate forms once data arrives */
  useEffect(() => {
    if (!data) return

    coreForm.setValues({
      inventoryAllocationStrategy:  data.inventoryAllocationStrategy ?? "HYBRID",
      buyerSmsNotificationsEnabled: data.buyerSmsNotificationsEnabled ?? false,
    })

    shortageForm.setValues({
      shortageDecisionPolicy:            data.shortageDecisionPolicy ?? "SEVERITY_BASED",
      shortageReviewThresholdPercent:    data.shortageReviewThresholdPercent ?? 5,
      shortageEscalationThresholdPercent: data.shortageEscalationThresholdPercent ?? 30,
      failedDeliveryFeePolicy:           data.failedDeliveryFeePolicy ?? "NEVER",
      terminationPolicy:                 data.terminationPolicy ?? "ESCALATION_LADDER",
      terminationMaxAttempts:            data.terminationMaxAttempts ?? 3,
      evidence:                          evidenceFromConfig(data.failureEvidenceRules),
    })

    idempotencyForm.setValues({
      keyTtlHours:          data.idempotency?.keyTtlHours ?? 24,
      requireOnMutation:    data.idempotency?.requireOnMutation ?? true,
      assignmentVersioning: data.idempotency?.assignmentVersioning ?? false,
    })

    smsForm.setValues({
      enabled:            data.smsConfig?.enabled ?? false,
      operatorPreference: data.smsConfig?.operatorPreference || "AUTO",
    })

    rolesForm.setValues({
      routePlannerRoles:             data.routePlannerRoles             ?? [],
      supplyChannelCoordinatorRoles: data.supplyChannelCoordinatorRoles ?? [],
      localChannelCoordinatorRoles:  data.localChannelCoordinatorRoles  ?? [],
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  if (isLoading) {
    return (
      <Stack gap="sm">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height={48} radius="md" />
        ))}
      </Stack>
    )
  }

  if (error) {
    return (
      <ListState state="error" title={t("common.error")} description={t("common.loadError")} onRetry={() => reload()} />
    )
  }

  return (
    <Tabs defaultValue="core" keepMounted={false}>
      <Tabs.List>
        <Tabs.Tab value="core" leftSection={<GearSixIcon size={16} />}>
          {t("governance.tabs.core")}
        </Tabs.Tab>
        <Tabs.Tab value="shortageFailure" leftSection={<WarningIcon size={16} />}>
          {t("governance.tabs.shortageFailure")}
        </Tabs.Tab>
        <Tabs.Tab value="idempotency" leftSection={<ShieldCheckIcon size={16} />}>
          {t("governance.tabs.idempotency")}
        </Tabs.Tab>
        <Tabs.Tab value="sms" leftSection={<ChatCircleIcon size={16} />}>
          {t("governance.tabs.sms")}
        </Tabs.Tab>
        <Tabs.Tab value="roles" leftSection={<UsersThreeIcon size={16} />}>
          {t("governance.tabs.roles")}
        </Tabs.Tab>
      </Tabs.List>

      {/* ── Panel 1: Core ───────────────────────────────── */}
      <Tabs.Panel value="core" pt="md">
        <Stack gap="sm">
          <SettingsFields code={SETTINGS_WRITE.governance} gap="sm">
            <Text size="sm" c="gray.7">
              {t("governance.core.crossRef")}
            </Text>
            <Select
              label={t("governance.core.fields.inventoryAllocationStrategy.label")}
              description={t("governance.core.fields.inventoryAllocationStrategy.description")}
              data={ALLOCATION_STRATEGIES.map((value) => ({
                value,
                label: t(`governance.core.options.allocationStrategy.${value}`),
              }))}
              allowDeselect={false}
              {...coreForm.getInputProps("inventoryAllocationStrategy")}
            />
            <Switch
              label={t("governance.core.fields.buyerSmsNotificationsEnabled.label")}
              description={t("governance.core.fields.buyerSmsNotificationsEnabled.description")}
              checked={coreForm.values.buyerSmsNotificationsEnabled}
              onChange={(e) =>
                coreForm.setFieldValue("buyerSmsNotificationsEnabled", e.currentTarget.checked)
              }
            />
          </SettingsFields>
          <SectionSave
            loading={mutations.submitting}
            onSave={() =>
              mutations.governance.update({
                inventoryAllocationStrategy:  coreForm.values.inventoryAllocationStrategy,
                buyerSmsNotificationsEnabled: coreForm.values.buyerSmsNotificationsEnabled,
              })
            }
          />
        </Stack>
      </Tabs.Panel>

      {/* ── Panel 2: Shortage & Failure ─────────────────── */}
      <Tabs.Panel value="shortageFailure" pt="md">
        <Stack gap="md">
          <SettingsFields code={SETTINGS_WRITE.governance} gap="md">
            {/* Shortage card */}
            <Card withBorder radius="md" p="md">
              <Title order={6} mb="sm">
                {t("governance.shortageFailure.shortageCard.title")}
              </Title>
              <Stack gap="sm">
                <Select
                  label={t("governance.shortageFailure.fields.shortageDecisionPolicy.label")}
                  description={t("governance.shortageFailure.fields.shortageDecisionPolicy.description")}
                  data={SHORTAGE_POLICIES.map((value) => ({
                    value,
                    label: t(`governance.shortageFailure.options.shortageDecisionPolicy.${value}`),
                  }))}
                  allowDeselect={false}
                  {...shortageForm.getInputProps("shortageDecisionPolicy")}
                />
                <Group grow>
                  <NumberInput
                    label={t("governance.shortageFailure.fields.shortageReviewThreshold.label")}
                    description={t("governance.shortageFailure.fields.shortageReviewThreshold.description")}
                    min={0}
                    max={100}
                    {...shortageForm.getInputProps("shortageReviewThresholdPercent")}
                  />
                  <NumberInput
                    label={t("governance.shortageFailure.fields.shortageEscalationThreshold.label")}
                    description={t("governance.shortageFailure.fields.shortageEscalationThreshold.description")}
                    min={0}
                    max={100}
                    {...shortageForm.getInputProps("shortageEscalationThresholdPercent")}
                  />
                </Group>
              </Stack>
            </Card>

            {/* Failure card */}
            <Card withBorder radius="md" p="md">
              <Title order={6} mb="sm">
                {t("governance.shortageFailure.failureCard.title")}
              </Title>
              <Stack gap="sm">
                <Select
                  label={t("governance.shortageFailure.fields.failedDeliveryFeePolicy.label")}
                  description={t("governance.shortageFailure.fields.failedDeliveryFeePolicy.description")}
                  data={FEE_POLICIES.map((value) => ({
                    value,
                    label: t(`governance.shortageFailure.options.feePolicy.${value}`),
                  }))}
                  allowDeselect={false}
                  {...shortageForm.getInputProps("failedDeliveryFeePolicy")}
                />
                <Select
                  label={t("governance.shortageFailure.fields.terminationPolicy.label")}
                  description={t("governance.shortageFailure.fields.terminationPolicy.description")}
                  data={TERMINATION_POLICIES.map((value) => ({
                    value,
                    label: t(`governance.shortageFailure.options.terminationPolicy.${value}`),
                  }))}
                  allowDeselect={false}
                  {...shortageForm.getInputProps("terminationPolicy")}
                />
                <NumberInput
                  label={t("governance.shortageFailure.fields.maxAttempts.label")}
                  min={1}
                  max={20}
                  allowDecimal={false}
                  {...shortageForm.getInputProps("terminationMaxAttempts")}
                />
                <Stack gap="xs">
                  <Text size="sm" fw={500}>{t("governance.shortageFailure.fields.failedDeliveryEvidence.label")}</Text>
                  <Text size="sm" c="gray.7">{t("governance.shortageFailure.fields.failedDeliveryEvidence.description")}</Text>
                  {EVIDENCE_TYPES.map((type) => (
                    <Switch
                      key={type}
                      label={t(`governance.shortageFailure.options.evidence.${type}`)}
                      checked={shortageForm.values.evidence[type]}
                      onChange={(e) =>
                        shortageForm.setFieldValue("evidence", {
                          ...shortageForm.values.evidence,
                          [type]: e.currentTarget.checked,
                        })
                      }
                    />
                  ))}
                </Stack>
              </Stack>
            </Card>

          </SettingsFields>
          <SectionSave
            loading={mutations.submitting}
            onSave={() =>
              mutations.governance.update({
                shortageDecisionPolicy:            shortageForm.values.shortageDecisionPolicy,
                shortageReviewThresholdPercent:    shortageForm.values.shortageReviewThresholdPercent,
                shortageEscalationThresholdPercent: shortageForm.values.shortageEscalationThresholdPercent,
                failedDeliveryFeePolicy:           shortageForm.values.failedDeliveryFeePolicy,
                terminationPolicy:                 shortageForm.values.terminationPolicy,
                terminationMaxAttempts:            shortageForm.values.terminationMaxAttempts,
                // Merge into the stored rules so failure types this form does not list keep their setting.
                failureEvidenceRules: EVIDENCE_TYPES.reduce(
                  (rules, type) => ({ ...rules, [type]: { ...rules[type], requirePhoto: shortageForm.values.evidence[type] } }),
                  { ...(data?.failureEvidenceRules ?? {}) } as IGovernanceConfig["failureEvidenceRules"],
                ),
              })
            }
          />
        </Stack>
      </Tabs.Panel>

      {/* ── Panel 3: Idempotency ─────────────────────────── */}
      <Tabs.Panel value="idempotency" pt="md">
        <Stack gap="sm">
          <SettingsFields code={SETTINGS_WRITE.governance} gap="sm">
            <NumberInput
              label={t("governance.idempotency.fields.idempotencyKeyTtl.label")}
              description={t("governance.idempotency.fields.idempotencyKeyTtl.description")}
              min={1}
              {...idempotencyForm.getInputProps("keyTtlHours")}
            />
            <Switch
              label={t("governance.idempotency.fields.requireOnMutation.label")}
              description={t("governance.idempotency.fields.requireOnMutation.description")}
              checked={idempotencyForm.values.requireOnMutation}
              onChange={(e) =>
                idempotencyForm.setFieldValue("requireOnMutation", e.currentTarget.checked)
              }
            />
            <Switch
              label={t("governance.idempotency.fields.assignmentVersioning.label")}
              description={t("governance.idempotency.fields.assignmentVersioning.description")}
              checked={idempotencyForm.values.assignmentVersioning}
              onChange={(e) =>
                idempotencyForm.setFieldValue("assignmentVersioning", e.currentTarget.checked)
              }
            />
          </SettingsFields>
          <SectionSave
            loading={mutations.submitting}
            onSave={() =>
              mutations.governance.update({
                idempotency: {
                  keyTtlHours:          idempotencyForm.values.keyTtlHours,
                  requireOnMutation:    idempotencyForm.values.requireOnMutation,
                  assignmentVersioning: idempotencyForm.values.assignmentVersioning,
                },
              })
            }
          />
        </Stack>
      </Tabs.Panel>

      {/* ── Panel 4: SMS ─────────────────────────────────── */}
      <Tabs.Panel value="sms" pt="md">
        <Stack gap="sm">
          <SettingsFields code={SETTINGS_WRITE.governance} gap="sm">
            <Switch
              label={t("governance.sms.fields.enabled.label")}
              checked={smsForm.values.enabled}
              onChange={(e) => smsForm.setFieldValue("enabled", e.currentTarget.checked)}
            />
            <Select
              label={t("governance.sms.fields.operatorPreference.label")}
              description={t("governance.sms.fields.operatorPreference.description")}
              data={SMS_OPERATORS.map((value) => ({
                value,
                label: t(`governance.sms.options.operator.${value}`),
              }))}
              allowDeselect={false}
              {...smsForm.getInputProps("operatorPreference")}
            />
          </SettingsFields>
          <SectionSave
            loading={mutations.submitting}
            onSave={() => {
              const prev = (data as IGovernanceConfig).smsConfig
              mutations.governance.update({
                smsConfig: {
                  ...prev,
                  enabled:            smsForm.values.enabled,
                  operatorPreference: smsForm.values.operatorPreference,
                },
              })
            }}
          />
        </Stack>
      </Tabs.Panel>

      {/* ── Panel 5: Roles ───────────────────────────────── */}
      <Tabs.Panel value="roles" pt="md">
        <Stack gap="sm">
          <SettingsFields code={SETTINGS_WRITE.governance} gap="sm">
            <Text size="sm" c="gray.7">
              {t("governance.roles.description")}
            </Text>
            <MultiSelect
              label={t("governance.roles.fields.routePlannerRoles.label")}
              data={roleOptions}
              {...rolesForm.getInputProps("routePlannerRoles")}
            />
            <MultiSelect
              label={t("governance.roles.fields.supplyChannelCoordinatorRoles.label")}
              data={roleOptions}
              {...rolesForm.getInputProps("supplyChannelCoordinatorRoles")}
            />
            <MultiSelect
              label={t("governance.roles.fields.localChannelCoordinatorRoles.label")}
              data={roleOptions}
              {...rolesForm.getInputProps("localChannelCoordinatorRoles")}
            />
          </SettingsFields>
          <SectionSave
            loading={mutations.submitting}
            onSave={() =>
              mutations.governance.update({
                routePlannerRoles:             rolesForm.values.routePlannerRoles,
                supplyChannelCoordinatorRoles: rolesForm.values.supplyChannelCoordinatorRoles,
                localChannelCoordinatorRoles:  rolesForm.values.localChannelCoordinatorRoles,
              })
            }
          />
        </Stack>
      </Tabs.Panel>
    </Tabs>
  )
}
